import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Thread } from '@/types/chatTypes'
import { FilePage } from '@/types/FilePages';
import { LatticeFile } from '@/types/latticeTypes';
interface UpdateThreadNameArgs {
    threadId: number;
    name: string;
}

interface CreateThreadFileArgs {
    threadId: number;
    latticeFileIds: number[];
}

interface DeleteThreadFileArgs {
    threadFileIds: number[];
}

export interface GetFilePagesParams {
    fileId: number;
};

export const chatApi = createApi({
    reducerPath: 'chatApi',
    baseQuery: fetchBaseQuery({
        baseUrl: 'https://api.getspect.ai/v3',
        credentials: 'include'
    }),
    tagTypes: ['Thread', 'Question', 'Document', 'Answer', 'FilePage'],
    endpoints: (builder) => ({
        getThreads: builder.query<Thread[], void>({
            query: () => '/thread',
            transformResponse: async (response: Thread[]) => {
                // Fetch sample thread if it doesn't exist in response
                if (!response.some(thread => thread.name === 'Sample Thread')) {
                    try {
                        const sampleResponse = await fetch('https://api.getspect.ai/v3/thread/sample', {
                            credentials: 'include'
                        });
                        
                        // Check if response is ok and has content
                        if (sampleResponse.ok && sampleResponse.headers.get('content-length') !== '0') {
                            const sampleThread = await sampleResponse.json();
                            if (sampleThread) {
                                return [...response, sampleThread];
                            }
                        }
                        // If no valid sample thread, return original response
                        return response;
                    } catch (error) {
                        return response;
                    }
                }
                return response;
            },
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({ type: 'Thread' as const, id })),
                        'Thread'
                    ]
                    : ['Thread']
        }),

        createThread: builder.mutation<number, string>({
            query: (name) => ({
                url: '/thread',
                method: 'POST',
                headers: {
                    'Content-Type': 'text/plain',
                },
                body: name
            }),
            invalidatesTags: ['Thread']
        }),

        updateThreadName: builder.mutation<void, UpdateThreadNameArgs>({
            query: ({ threadId, name }) => ({
                url: `/thread/${threadId}`,
                method: 'PUT',
                body: { name }
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Thread', id: arg.threadId }]
        }),

        deleteThread: builder.mutation<void, number>({
            query: (threadId) => ({
                url: `/thread/${threadId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Thread']
        }),

        createThreadFile: builder.mutation<void, CreateThreadFileArgs>({
            query: ({ threadId, latticeFileIds }) => ({
                url: `/thread/${threadId}/add-files`,
                method: 'POST',
                body: latticeFileIds 
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Thread', id: arg.threadId }]
        }),

        getChatFile: builder.query<LatticeFile, number>({
            query: (fileId) => `/chat-file/${fileId}`,
        }),

        getAllChatFiles: builder.query<LatticeFile[], void>({
            query: () => '/chat-file',
            providesTags: ['Document']
        }),

        deleteThreadFile: builder.mutation<void, DeleteThreadFileArgs>({
            query: ({ threadFileIds }) => ({
                url: `/thread-file`,
                method: 'DELETE',
                body: threadFileIds
            }),
            async onQueryStarted({ threadFileIds }, { dispatch, queryFulfilled, getState }) {
                const patchResult = dispatch(
                    chatApi.util.updateQueryData('getThreads', undefined, (draft) => {
                        return draft.map(thread => ({
                            ...thread,
                            files: thread.files.filter(file => !threadFileIds.includes(file.id))
                        }));
                    })
                );
                try {
                    await queryFulfilled;
                } catch (error) {
                    patchResult.undo();
                    throw error;
                }
            },
            invalidatesTags: ['Thread']
        }),

        getFilePages: builder.query<FilePage[], GetFilePagesParams>({
            query: ({ fileId }) => ({
                url: `/chat-file/${fileId}/pdf`
            }),
            providesTags: (result, _error, _arg) =>
                result
                  ? [...result.map(({ id }) => ({ type: 'FilePage' as const, id })), 'FilePage']
                  : ['FilePage'],
        }),

        addFilesToLatticeThread: builder.mutation<{ id: number }, number[]>({
            query: (fileIds) => ({
                url: '/maintenance/lattice',
                method: 'POST',
                body: fileIds
            }),
            invalidatesTags: ['Thread']
        }),
    })
})

export const {
    useGetThreadsQuery,
    useCreateThreadMutation,
    useUpdateThreadNameMutation,
    useDeleteThreadMutation,
    useCreateThreadFileMutation,
    useGetChatFileQuery,
    useDeleteThreadFileMutation,
    useGetAllChatFilesQuery,
    useGetFilePagesQuery,
    useAddFilesToLatticeThreadMutation,
} = chatApi
